import React, { useState } from "react";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import CalendlyButton from "../components/site/CalendlyButton";
import { api, formatApiError } from "../lib/api";
import useSEO from "../lib/useSEO";
import { toast } from "sonner";

export default function ContactPage() {
  useSEO({
    title: "Contact — Let's Connect",
    description: "Tell us about your brand, your audience and what success looks like. Call 253-331-8487 or book a 30-minute strategy session.",
    path: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteShell>
      <section className="pt-32 pb-20 border-b border-white/5" data-testid="contact-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
          <div>
            <div className="kicker mb-6">Contact</div>
            <h1 className="h-display text-white text-[48px] sm:text-[72px] lg:text-[96px] leading-[0.9]">
              LET'S<br />
              <span className="text-[#d4af37]">CONNECT.</span>
            </h1>
          </div>
          <p className="text-white/70 max-w-[480px] text-[16px] leading-relaxed">
            Tell us about your brand, your audience and what success looks like.
            We'll respond within one business day with a path forward.
          </p>
        </div>
      </section>

      <section className="py-24" data-testid="contact-section">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full Name *" testId="contact-name">
                  <input className="lux-input" value={form.name} onChange={onChange("name")} placeholder="Jane Doe" data-testid="contact-input-name" />
                </Field>
                <Field label="Email *" testId="contact-email">
                  <input type="email" className="lux-input" value={form.email} onChange={onChange("email")} placeholder="you@brand.com" data-testid="contact-input-email" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Phone" testId="contact-phone">
                  <input className="lux-input" value={form.phone} onChange={onChange("phone")} placeholder="253-555-0100" data-testid="contact-input-phone" />
                </Field>
                <Field label="Company" testId="contact-company">
                  <input className="lux-input" value={form.company} onChange={onChange("company")} placeholder="Your Company" data-testid="contact-input-company" />
                </Field>
              </div>
              <Field label="Message *" testId="contact-message">
                <textarea
                  className="lux-input"
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell us about your campaign goals…"
                  rows={5}
                  data-testid="contact-input-message"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold btn-label disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {submitting ? "Sending…" : done ? "Sent" : "Send Message"} <ArrowRight size={16} strokeWidth={1.6} />
              </button>
            </form>
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-10">
            <div data-testid="contact-info-phone">
              <div className="kicker mb-3">Call</div>
              <a href="tel:2533318487" className="flex items-center gap-3 text-white text-[22px] font-display">
                <Phone size={18} strokeWidth={1.3} className="text-[#d4af37]" /> 253-331-8487
              </a>
            </div>
            <div data-testid="contact-info-email">
              <div className="kicker mb-3">Email</div>
              <a href="mailto:info@raeenterprisellc.com" className="flex items-center gap-3 text-white text-[18px]">
                <Mail size={16} strokeWidth={1.3} className="text-[#d4af37]" /> info@raeenterprisellc.com
              </a>
            </div>
            <div data-testid="contact-info-location">
              <div className="kicker mb-3">Visit</div>
              <p className="flex items-center gap-3 text-white">
                <MapPin size={16} strokeWidth={1.3} className="text-[#d4af37]" /> Seattle, Washington
              </p>
            </div>
            <div className="bg-[#111111] border border-[#d4af37]/20 p-8">
              <div className="kicker mb-3">Book a Strategy Call</div>
              <p className="text-white/70 text-[14px] mb-5">
                Prefer to talk it through? Schedule a private 30-minute strategy session.
              </p>
              <CalendlyButton variant="outline" testId="contact-strategy-call-btn">
                Schedule Now
              </CalendlyButton>
            </div>
          </aside>
        </div>
      </section>
    </SiteShell>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={`field-${testId}`}>
      <span className="kicker mb-2 block">{label}</span>
      {children}
    </label>
  );
}
